import { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
// Re-export the pure-logic state machine so callers (and unit tests) can
// import everything from one path.
export {
  INITIAL_GUARD_STATE,
  requestNavigateAction,
  confirmAction,
  cancelAction,
} from './guardStateMachine';
export type { GuardState } from './guardStateMachine';

/**
 * Unsaved-change guard for the LandingPageEditor.
 *
 * Behaviour:
 *   1. Browser-level: when `dirty === true`, attach a `beforeunload` handler
 *      so the browser prompts on tab close / reload.
 *   2. In-app: when `dirty === true` AND the route is about to change, raise
 *      a `pendingNavigation` so the caller can render a confirmation dialog
 *      (`Discard changes / Continue editing`). When the user confirms, the
 *      caller calls `confirmNavigation()` to actually navigate.
 *
 * react-router v6 dropped `useBlocker` from the public API and re-added it
 * in v6.4 as `unstable_useBlocker`. To avoid that volatile surface and stay
 * portable, this hook layers on top of `useLocation`/`useNavigate` by
 * intercepting same-tab `popstate` events and providing a `requestNavigate`
 * wrapper for in-app links to call instead of `navigate()` directly. The
 * editor toolbar uses `requestNavigate` for Cancel; other in-app links the
 * user might click while the editor is mounted route through the standard
 * `Link` element. For those, the navigation guard relies on the
 * `pendingNavigation` capture inside an effect listening for location
 * changes — if `dirty` flips true and the location is about to change, we
 * pop the dialog.
 *
 * Public surface:
 *   - `dirty`             — pass `true` while the user has unsaved edits
 *   - `pendingNavigation` — `null` until the user attempts to navigate away
 *   - `confirmNavigation()` — perform the deferred navigation
 *   - `cancelNavigation()`  — keep the user where they are
 *   - `requestNavigate(to)` — programmatic guarded navigation
 */
export interface UnsavedChangeGuardApi {
  dirty: boolean;
  pendingNavigation: string | null;
  confirmNavigation: () => void;
  cancelNavigation: () => void;
  requestNavigate: (to: string) => void;
  /** Mark the layout dirty / clean. The hook does not track state itself; the
   *  editor owns the dirty flag and threads it in via the `dirty` param. */
}

export function useUnsavedChangeGuard(dirty: boolean): UnsavedChangeGuardApi {
  const location = useLocation();
  const navigate = useNavigate();
  const [pending, setPending] = useState<string | null>(null);
  // Track the path the user was on when the editor mounted so location
  // changes during edit mode can be intercepted. We don't try to block all
  // navigations — only ones AWAY from the editor's host route.
  const editorPathRef = useRef(location.pathname);

  // beforeunload — covers tab close / reload / external links.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // The string value is ignored by modern browsers — they show a
      // generic prompt — but assigning to `returnValue` is still required
      // for the prompt to fire at all in Chromium / Firefox.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  // In-app navigation interception via `popstate`. When the user uses
  // the browser back button while dirty, we capture the intended target,
  // re-push the editor's path, and surface it as `pendingNavigation`.
  useEffect(() => {
    if (!dirty) return;
    const handler = () => {
      const target = window.location.pathname + window.location.search;
      if (target !== editorPathRef.current) {
        // Defer to next tick so React Router's navigation state settles
        // before we re-push.
        setPending(target);
        window.history.pushState(null, '', editorPathRef.current);
      }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, [dirty]);

  const confirmNavigation = useCallback(() => {
    if (pending) {
      // Clear the dirty flag first by signalling to the caller (they own
      // the state). The caller should have already cleared it before
      // calling confirmNavigation, but we do not enforce it here.
      navigate(pending);
      setPending(null);
    }
  }, [pending, navigate]);

  const cancelNavigation = useCallback(() => {
    setPending(null);
  }, []);

  const requestNavigate = useCallback(
    (to: string) => {
      if (dirty) {
        setPending(to);
      } else {
        navigate(to);
      }
    },
    [dirty, navigate],
  );

  return {
    dirty,
    pendingNavigation: pending,
    confirmNavigation,
    cancelNavigation,
    requestNavigate,
  };
}
